// src/pages/ArticlesPage/ArticlesPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Navigation from '../../components/Navigation/Navigation';
import ArticleCard from '../../components/ArticleCard/ArticleCard';
import ArticleReader from '../../components/ArticleReader/ArticleReader';
import { ArticlesConfig, ArticlesIndex, Article, CategorieArticle, ArticleContent, SearchFiltersArticles } from '../../data/types';
import articlesConfig from '../../data/articles-config.json';
import articlesIndex from '../../data/articles-index.json';
import { Analytics } from '../../utils/analytics';
import './ArticlesPage.css';
import SEO from '../../components/SEO/SEO';

const ArticlesPage: React.FC = () => {
  const { articleId } = useParams<{ articleId?: string }>();
  const navigate = useNavigate();
  const [config] = useState<ArticlesConfig>(articlesConfig as ArticlesConfig);
  const [index] = useState<ArticlesIndex>(articlesIndex as ArticlesIndex);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [loadingArticle, setLoadingArticle] = useState<string | null>(null);
  const [searchFilters, setSearchFilters] = useState<SearchFiltersArticles>({
    query: '',
    selectedCategory: '',
    selectedTags: []
  });

  // Scroll vers le haut quand la page se charge
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fonction pour charger le contenu d'un article
  const loadArticleContent = async (articleId: string): Promise<ArticleContent | null> => {
    const articleMeta = index.articles.find(a => a.id === articleId);
    if (!articleMeta) return null;

    try {
      setLoadingArticle(articleId);
      // Fetch du fichier JSON de l'article depuis le dossier public
      const response = await fetch(`/data/articles/${articleMeta.fichier}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const content = await response.json();
      return content as ArticleContent;
    } catch (error) {
      console.error(`Erreur lors du chargement de l'article ${articleId}:`, error);
      return null;
    } finally {
      setLoadingArticle(null);
    }
  };

  // Fonction pour obtenir la catégorie d'un article
  const getCategorieForArticle = (article: Article): CategorieArticle => {
    return config.categories.find(cat => cat.id === article.categorie) || config.categories[0];
  };

  // Logique de recherche et filtrage
  const filteredArticles = useMemo(() => {
    return index.articles.filter(article => {
      // Filtre par requête de recherche
      if (searchFilters.query) {
        const query = searchFilters.query.toLowerCase();
        const matchesTitle = article.titre.toLowerCase().includes(query);
        const matchesSubtitle = article.sousTitre?.toLowerCase().includes(query) || false;
        const matchesTags = article.tags.some(tag => tag.toLowerCase().includes(query));
        const matchesApercu = article.apercu?.toLowerCase().includes(query) || false;
        
        if (!matchesTitle && !matchesSubtitle && !matchesTags && !matchesApercu) {
          return false;
        }
      }

      // Afficher seulement les articles publiés
      return article.statut === 'publié';
    });
  }, [index.articles, searchFilters]);

  // Gestionnaires d'événements
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilters(prev => ({
      ...prev,
      query: e.target.value
    }));
  };

  const clearFilters = () => {
    setSearchFilters({
      query: '',
      selectedCategory: '',
      selectedTags: []
    });
  };

  const openArticleById = async (id: string) => {
    const articleMeta = index.articles.find(a => a.id === id);
    if (!articleMeta) return;
    
    // Track article view
    Analytics.trackArticleView(articleMeta.id, articleMeta.titre);
    
    // Charger le contenu de l'article
    const content = await loadArticleContent(articleMeta.id);
    if (content) {
      const fullArticle: Article = {
        ...articleMeta,
        content
      };
      setSelectedArticle(fullArticle);
      window.scrollTo(0, 0);
    } else {
      alert('Erreur lors du chargement de l\'article.');
    }
  };

  const openArticle = async (articleMeta: Article) => {
    // Naviguer vers l'URL de l'article
    navigate(`/articles/${articleMeta.id}`);
  };

  const closeArticle = () => {
    setSelectedArticle(null);
    // Revenir à la liste des articles
    navigate('/articles');
  };

  // Charger automatiquement l'article si l'ID est dans l'URL ou réinitialiser si pas d'ID
  useEffect(() => {
    if (articleId) {
      // Si on a un ID d'article dans l'URL, charger l'article
      if (!selectedArticle || selectedArticle.id !== articleId) {
        const articleMeta = index.articles.find(a => a.id === articleId);
        if (articleMeta && articleMeta.statut === 'publié') {
          openArticleById(articleId);
        } else {
          // Article non trouvé ou non publié, rediriger vers la liste
          navigate('/articles', { replace: true });
        }
      }
    } else {
      // Si pas d'ID dans l'URL, s'assurer qu'aucun article n'est sélectionné
      if (selectedArticle) {
        setSelectedArticle(null);
      }
    }
  }, [articleId, selectedArticle, index.articles, navigate]);

  // Si un article est sélectionné, afficher le lecteur
    if (selectedArticle && selectedArticle.content) {
      const categorie = getCategorieForArticle(selectedArticle);
      return (
        <div className="articles-page">
          <Navigation />
          
          <SEO 
            title={`${selectedArticle.titre} - Mâhî Bâyân`}
            description={selectedArticle.apercu || selectedArticle.sousTitre || `Article islamique : ${selectedArticle.titre}`}
            url={`https://mahi-bayan.netlify.app/articles/${selectedArticle.id}`}
          />
          
          <div className="page-content">
          <ArticleReader 
            article={selectedArticle}
            categorie={categorie}
            onBack={closeArticle}
          />
        </div>
      </div>
    );
  }

  // Affichage du chargement si un article est en cours de chargement
  if (loadingArticle) {
    return (
      <div className="articles-page">
        <Navigation />
        <div className="page-content">
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Chargement de l'article...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Affichage de la liste des articles
    return (
      <div className="articles-page">
        <Navigation />
        
        <SEO 
          title="Articles islamiques - Mâhî Bâyân"
          description="Découvrez nos articles authentiques : traductions de paroles de savants, enseignements islamiques et analyses approfondies. 3 articles disponibles."
          url="https://mahi-bayan.netlify.app/articles"
        />
        
        <div className="page-content">
        <Header 
          mainTitle={config.header.mainTitle}
          subtitle={config.header.subtitle}
        />

        <main className="content">
          {/* Barre de recherche et filtres */}
          <div className="search-section">
            <div className="search-container">
              <div className="search-input-wrapper">
                <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Rechercher dans les articles..."
                  value={searchFilters.query}
                  onChange={handleSearchChange}
                />
                {searchFilters.query && (
                  <button 
                    className="clear-search"
                    onClick={() => setSearchFilters(prev => ({ ...prev, query: '' }))}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>


            {/* Bouton pour effacer les filtres */}
            {searchFilters.query && (
              <div className="clear-filters-container">
                <button className="clear-filters-button" onClick={clearFilters}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
                    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Effacer tous les filtres
                </button>
              </div>
            )}
          </div>

          {/* Résultats */}
          <div className="results-section">
            <div className="results-header">
              <h3>
                {filteredArticles.length === 0 ? 'Aucun article trouvé' : 
                 filteredArticles.length === 1 ? '1 article trouvé' :
                 `${filteredArticles.length} articles trouvés`}
              </h3>
              {filteredArticles.length !== index.articles.filter(a => a.statut === 'publié').length && (
                <span className="results-count">
                  sur {index.articles.filter(a => a.statut === 'publié').length} au total
                </span>
              )}
            </div>

            {filteredArticles.length === 0 ? (
              <div className="no-results">
                <div className="no-results-icon">📚</div>
                <h4>Aucun article ne correspond à votre recherche</h4>
                <p>Essayez de modifier vos critères de recherche ou consultez tous nos articles.</p>
                <button className="show-all-button" onClick={clearFilters}>
                  Voir tous les articles
                </button>
              </div>
            ) : (
              <div className="articles-grid">
                {filteredArticles.map(article => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    categorie={getCategorieForArticle(article)}
                    onClick={() => openArticle(article)}
                  />
                ))}
              </div>
            )}
          </div>
        </main>

        <Footer texts={[]} />
      </div>
    </div>
  );
};

export default ArticlesPage;
